import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const TeamProductivity = ({ data, loading }) => {
  const activityIcons = {
    create: 'Plus',
    update: 'Edit',
    delete: 'Trash2',
    login: 'LogIn',
    logout: 'LogOut',
    invite: 'UserPlus',
    role_change: 'Shield',
    permission_change: 'Key'
  };

  const activityColors = {
    create: 'text-success',
    update: 'text-primary',
    delete: 'text-error',
    login: 'text-info',
    logout: 'text-muted-foreground',
    invite: 'text-accent',
    role_change: 'text-warning',
    permission_change: 'text-secondary'
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-1">{payload?.[0]?.payload?.user || payload?.[0]?.payload?.action}</p>
          <p className="text-xs text-muted-foreground">
            Activities: {payload?.[0]?.value}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Activity" size={24} className="text-primary" />
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground">Team Productivity</h3>
          <p className="text-xs text-muted-foreground">Activity metrics and recent actions</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity by User Chart */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Activity by Team Member</h4>
          {data?.activityByUser?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Icon name="Users" size={48} className="mb-2 opacity-50" />
              <p className="text-sm">No activity data available</p>
            </div>
          ) : (
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.activityByUser?.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis type="number" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }} />
                  <YAxis 
                    dataKey="user" 
                    type="category" 
                    tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
                    width={100}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="var(--color-primary)" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Recent Activity List */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Recent Activity</h4>
          {data?.recentActivity?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Icon name="Activity" size={48} className="mb-2 opacity-50" />
              <p className="text-sm">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data?.recentActivity?.map((activity, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className={`mt-0.5 ${activityColors?.[activity?.action] || 'text-muted-foreground'}`}>
                    <Icon name={activityIcons?.[activity?.action] || 'Circle'} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {activity?.userProfiles?.fullName || 'Unknown User'}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {activity?.action?.replace('_', ' ')}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(activity?.createdAt)?.toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Activity Type Summary */}
      {data?.activityByAction?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Activity Breakdown</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data?.activityByAction?.map((item, index) => (
              <div key={index} className="bg-muted/30 rounded-lg p-3 text-center">
                <div className={`flex items-center justify-center mb-2 ${activityColors?.[item?.action] || 'text-muted-foreground'}`}>
                  <Icon name={activityIcons?.[item?.action] || 'Circle'} size={20} />
                </div>
                <p className="text-xs text-muted-foreground capitalize mb-1">
                  {item?.action?.replace('_', ' ')}
                </p>
                <p className="text-lg font-bold text-foreground">{item?.count}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamProductivity;